import { useMemo, useState } from "react";
import { ArrowRight, Check, Plus } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

// Compact multi-select finder. Options, metaobject ids and the
// /collections/all destination mirror riversol.com's live "Find your
// perfect match" form; multiple values are sent as repeated params
// (Shopify OR-filter). Explicit #0F2E2E because PopoverContent portals
// outside `.home-scope`.
const SHOP_ALL = "https://www.riversol.com/collections/all";
const SKIN_PARAM = "filter.p.m.custom.suited_to";
const TARGET_PARAM = "filter.p.m.custom.targets";

export const SKIN = [
  ["Normal to Dry", "gid://shopify/Metaobject/183007477807"],
  ["Combination", "gid://shopify/Metaobject/183007543343"],
  ["Oily", "gid://shopify/Metaobject/183007576111"],
  ["Very Dry", "gid://shopify/Metaobject/183007608879"],
  ["Acne-prone", "gid://shopify/Metaobject/192180781103"],
  ["Redness-prone", "gid://shopify/Metaobject/214830350383"],
  ["Mature Skin", "gid://shopify/Metaobject/232811003951"],
];

export const CONCERNS = [
  ["Dehydration", "gid://shopify/Metaobject/183002759215"],
  ["Signs of Aging", "gid://shopify/Metaobject/183002791983"],
  ["Uneven Skin Tone", "gid://shopify/Metaobject/192176685103"],
  ["Fine Lines", "gid://shopify/Metaobject/192176717871"],
  ["Dullness", "gid://shopify/Metaobject/192176783407"],
  ["Eye Bags", "gid://shopify/Metaobject/192177045551"],
  ["Textural Irregularities", "gid://shopify/Metaobject/192177111087"],
  ["Visible Redness", "gid://shopify/Metaobject/192177274927"],
  ["Irritated Skin", "gid://shopify/Metaobject/192177340463"],
  ["Sun protection", "gid://shopify/Metaobject/192177635375"],
  ["Puffiness", "gid://shopify/Metaobject/192178061359"],
  ["Dark Circles", "gid://shopify/Metaobject/192178094127"],
  ["Cleansing", "gid://shopify/Metaobject/192178159663"],
  ["Dark Spots", "gid://shopify/Metaobject/192178520111"],
  ["Sagging", "gid://shopify/Metaobject/192178552879"],
  ["Acne", "gid://shopify/Metaobject/195734765615"],
  ["Dry, Cracked Hands", "gid://shopify/Metaobject/257307476015"],
];

const Pill = ({ children, muted, className = "" }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-1 text-[12px] leading-none whitespace-nowrap ${
      muted ? "border border-[#0F2E2E]/30 text-[#0F2E2E]" : "bg-[#0F2E2E] text-white"
    } ${className}`}
  >
    {children}
  </span>
);

const Summary = ({ labels }) => (
  <span className="flex items-center gap-1.5 min-w-0" data-testid="finder-summary-pills">
    <Pill className="truncate max-w-[150px]">{labels[0]}</Pill>
    {labels[1] && <Pill className="hidden sm:inline-flex truncate max-w-[150px]">{labels[1]}</Pill>}
    {labels.length > 2 && <Pill muted className="hidden sm:inline-flex">+{labels.length - 2}</Pill>}
    {labels.length > 1 && <Pill muted className="sm:hidden">+{labels.length - 1}</Pill>}
  </span>
);

const Selector = ({ id, step, label, placeholder, options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const labels = options.filter(([, gid]) => value.includes(gid)).map(([l]) => l);
  const toggle = (gid) => onChange(value.includes(gid) ? value.filter((v) => v !== gid) : [...value, gid]);
  const done = value.length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`finder-${id}-trigger`}
          data-count={value.length}
          className={`group w-full text-left bg-white rounded-2xl border px-5 py-4 flex items-center gap-4 outline-none transition-[border-color,box-shadow] focus-visible:ring-2 focus-visible:ring-[#0F2E2E]/25 shadow-[0_1px_2px_rgba(15,46,46,0.05),0_12px_30px_-20px_rgba(15,46,46,0.35)] ${
            open || done ? "border-[#0F2E2E]/45" : "border-[#0F2E2E]/25 hover:border-[#0F2E2E]/45"
          } ${open ? "shadow-[0_16px_36px_-18px_rgba(15,46,46,0.45)]" : "hover:shadow-[0_16px_36px_-20px_rgba(15,46,46,0.4)]"}`}
        >
          <span
            className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-mono text-[11px] tracking-[0.1em] transition-colors ${
              done ? "bg-[#0F2E2E] text-white" : "border border-[#0F2E2E]/30 text-[#0F2E2E]"
            }`}
            aria-hidden
          >
            {done ? <Check size={13} strokeWidth={2.2} /> : step}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-500">{label}</span>
            <span className="mt-1.5 block min-w-0">
              {value.length === 0 ? (
                <span className="text-[14px] text-neutral-700">{placeholder}</span>
              ) : (
                <Summary labels={labels} />
              )}
            </span>
          </span>
          <span
            className={`shrink-0 w-7 h-7 rounded-full border flex items-center justify-center transition-colors ${
              open || done
                ? "bg-[#0F2E2E] border-[#0F2E2E] text-white"
                : "border-[#0F2E2E]/30 text-[#0F2E2E] group-hover:border-[#0F2E2E] group-hover:bg-[#0F2E2E] group-hover:text-white"
            }`}
            aria-hidden
          >
            <Plus size={13} strokeWidth={1.8} className={`transition-transform ${open ? "rotate-45" : ""}`} />
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={10}
        collisionPadding={16}
        data-testid={`finder-${id}-panel`}
        className="w-[min(92vw,460px)] p-0 rounded-2xl border-neutral-200 bg-white shadow-[0_24px_60px_-24px_rgba(15,46,46,0.35)] font-sans"
      >
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-neutral-100">
          <div>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-500">{label}</div>
            <div className="mt-0.5 text-[13px] text-neutral-700" data-testid={`finder-${id}-count`}>
              {value.length === 0 ? "Select as many as apply" : `${value.length} selected`}
            </div>
          </div>
          {value.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              data-testid={`finder-${id}-clear`}
              className="text-[12px] text-neutral-500 underline underline-offset-4 decoration-neutral-300 hover:text-[#0F2E2E] hover:decoration-[#0F2E2E] transition-colors"
            >
              Clear
            </button>
          )}
        </div>
        <div className="px-5 py-4 flex flex-wrap gap-2 max-h-[300px] overflow-y-auto" role="group" aria-label={label}>
          {options.map(([opt, gid], idx) => {
            const on = value.includes(gid);
            return (
              <button
                key={gid}
                type="button"
                role="checkbox"
                aria-checked={on}
                onClick={() => toggle(gid)}
                data-testid={`finder-option-${id}-${idx}`}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-[13px] leading-none transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[#0F2E2E]/30 ${
                  on
                    ? "bg-[#0F2E2E] border-[#0F2E2E] text-white"
                    : "border-neutral-300 text-neutral-800 hover:border-[#0F2E2E] hover:text-[#0F2E2E]"
                }`}
              >
                {on && <Check size={12} strokeWidth={2.2} />}
                {opt}
              </button>
            );
          })}
        </div>
        <div className="px-5 py-3 border-t border-neutral-100 flex items-center justify-end">
          <button
            type="button"
            onClick={() => setOpen(false)}
            data-testid={`finder-${id}-done`}
            className="inline-flex items-center gap-2 rounded-full bg-[#0F2E2E] text-white px-5 py-2.5 text-[11px] tracking-[0.14em] uppercase font-medium hover:bg-[#1A4444] transition-colors"
          >
            Done
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const QuickFinder = () => {
  const [skin, setSkin] = useState([]);
  const [targets, setTargets] = useState([]);
  const ready = skin.length + targets.length > 0;

  const href = useMemo(() => {
    const p = new URLSearchParams();
    skin.forEach((g) => p.append(SKIN_PARAM, g));
    targets.forEach((g) => p.append(TARGET_PARAM, g));
    const q = p.toString();
    return q ? `${SHOP_ALL}?${q}` : SHOP_ALL;
  }, [skin, targets]);

  const ctaClass =
    "inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[12px] tracking-[0.14em] uppercase font-medium transition-colors whitespace-nowrap";

  return (
    <section className="bg-[#EDF3F3] border-b border-[#0F2E2E]/10" data-testid="home-quick-finder">
      <div className="max-w-[1600px] mx-auto px-5 sm:px-6 lg:px-12 xl:px-16 py-10 lg:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-start">
          {/* LEFT — intro */}
          <div className="lg:col-span-4">
            <div className="hidden font-mono text-[10px] tracking-[0.24em] uppercase text-[#0F2E2E]/70">Quick start</div>
            <h2 className="mt-2.5 text-[26px] sm:text-[28px] lg:text-[32px] leading-[1.08] tracking-[-0.02em] text-neutral-900">
              Find what works for <span className="italic text-[#0F2E2E]">your skin.</span>
            </h2>
            <p className="mt-3 text-[14px] leading-relaxed text-neutral-600 max-w-[360px]">
              Dermatologist-developed skincare for every step, designed to deliver visible results while respecting
              sensitive skin.
            </p>
          </div>

          {/* RIGHT — selectors + CTA */}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="finder-row">
              <Selector
                id="skin"
                step="01"
                label="Your skin"
                placeholder="Select skin profile"
                options={SKIN}
                value={skin}
                onChange={setSkin}
              />
              <Selector
                id="concern"
                step="02"
                label="You want to target"
                placeholder="Select concerns"
                options={CONCERNS}
                value={targets}
                onChange={setTargets}
              />
            </div>

            <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4 flex-wrap">
                {ready ? (
                  <a
                    href={href}
                    data-testid="finder-cta"
                    data-state="active"
                    className={`${ctaClass} group bg-[#0F2E2E] text-white hover:bg-[#1A4444]`}
                  >
                    Get My Formulations
                    <ArrowRight size={15} strokeWidth={1.8} className="transition-transform group-hover:translate-x-0.5" />
                  </a>
                ) : (
                  <button
                    type="button"
                    disabled
                    aria-disabled="true"
                    data-testid="finder-cta"
                    data-state="inactive"
                    className={`${ctaClass} border border-[#0F2E2E]/40 text-[#0F2E2E] cursor-not-allowed bg-transparent`}
                  >
                    Get My Formulations
                    <ArrowRight size={15} strokeWidth={1.8} />
                  </button>
                )}
                <span className="text-[12px] text-[#0F2E2E]/70" data-testid="finder-status">
                  {ready
                    ? `${skin.length} skin profile${skin.length === 1 ? "" : "s"} · ${targets.length} concern${
                        targets.length === 1 ? "" : "s"
                      }`
                    : "Choose as many as apply."}
                </span>
              </div>
              <a
                href="https://www.riversol.com/pages/skin-analyzer"
                data-testid="finder-analyzer-link"
                className="group inline-flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase font-medium text-[#0F2E2E]"
              >
                <span className="text-[#0F2E2E]/70 normal-case tracking-normal font-normal">Not sure?</span>
                <span className="underline underline-offset-4 decoration-[#0F2E2E]/40 group-hover:decoration-[#0F2E2E] transition-colors">
                  Take the Skin Analyzer
                </span>
                <ArrowRight size={13} strokeWidth={1.8} className="transition-transform group-hover:translate-x-0.5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuickFinder;
